import { Clock } from "lucide-react";
import {
  linkPresence,
  machineName,
  presenceColor,
  presenceLabel,
  waitingBannerText,
  waitingForMachine,
  whereLabel,
} from "@/lib/foreign-agents";
import type { AgentWithContext } from "@/lib/types";
import { cn } from "@/lib/utils";

type ForeignAgent = Pick<AgentWithContext, "kind" | "link" | "pendingTurns">;

/** The presence dot: green while the machine is connected, muted otherwise. */
export function PresenceDot({ agent, className }: { agent: Pick<AgentWithContext, "link">; className?: string }) {
  const presence = linkPresence(agent.link);
  return (
    <span
      aria-hidden
      className={cn("inline-block size-1.5 shrink-0 rounded-full", className)}
      style={{ backgroundColor: presenceColor(presence) }}
    />
  );
}

/**
 * A foreign agent's line in the Agents list, where a project would be: which machine,
 * whether it is there, and — offline with work queued — that the work waits for it.
 */
export function ForeignRowLine({ agent, now }: { agent: ForeignAgent; now: number }) {
  if (agent.kind !== "foreign") return null;
  const presence = linkPresence(agent.link);
  const waiting = waitingForMachine(agent);
  return (
    <p className="flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
      <PresenceDot agent={agent} />
      <span className="truncate">
        {presence === "unpaired" ? "not paired yet" : `${whereLabel(agent.link)} · ${presenceLabel(agent.link, now)}`}
      </span>
      {waiting > 0 ? (
        <span className="shrink-0 font-medium text-waiting">· waiting for {machineName(agent.link)}</span>
      ) : null}
    </p>
  );
}

/**
 * Above the composer while messages wait for a machine that is not connected: they are
 * queued, not lost, and run when `board-link` comes back.
 */
export function ForeignWaitingBanner({ agent }: { agent: ForeignAgent }) {
  const waiting = waitingForMachine(agent);
  if (waiting === 0) return null;
  return (
    <div className="mx-auto w-full max-w-3xl px-4 win-md:px-6">
      <p className="mb-1.5 flex items-start gap-1.5 rounded-md border border-waiting/40 bg-waiting/10 px-2.5 py-1.5 text-[12px] text-foreground/90">
        <Clock className="mt-px size-3.5 shrink-0 text-waiting" />
        <span>
          {agent.link?.paired
            ? waitingBannerText(waiting, machineName(agent.link))
            : `${waiting} ${waiting === 1 ? "message waits" : "messages wait"} for a machine — pair one with board-link and ${waiting === 1 ? "it runs" : "they run"} there.`}
        </span>
      </p>
    </div>
  );
}
